import React, { createContext, useEffect, useState } from "react";
import GlobalHeader from "../../components/header";
import Footer from "../../components/footer";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import useSnack from "../../hooks/useSnack";
import useApi from "../../hooks/useApi";
import SnackAlert from "../../components/SnackAlert";
import FilterOwnProjects from "../../components/Drawer/FilterOwnProjects";
import CardElement from "../../components/CardElement";
import ContainerCardsOwn from "../../components/ContainerCardsOwn";
import ActionsNav from "../../components/ActionsNav";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import { validateMinMaxIsValid } from "../../utils/validations";

export const ManageProjectContext = createContext();

function MyProjects() {
  // DATA
  const [mainData, setMainData] = useState(false);
  const [filteredData, setFilteredData] = useState(false);
  const [paginatedData, setPaginatedData] = useState(false);

  // STATES
  const [page, setPage] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false);
  const [backdropLoading, setBackdropLoading] = useState(false);
  const [expanded, setExpanded] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filtersSettings, setFiltersSettings] = useState({
    price: {
      min: "",
      max: "",
    },
    profitability: {
      min: "",
      max: "",
    },
    days: {
      min: "",
      max: "",
    },
    objective: {
      min: "",
      max: "",
    },
  });
  const ITEMS_PER_PAGE = 8;

  // UTILS
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const openMenuOrderBy = Boolean(anchorEl);
  const [openDrawer, setOpenDrawer] = useState(false);

  const handlerResetFilters = () => {
    setFiltersSettings({
      price: {
        min: "",
        max: "",
      },
      profitability: {
        min: "",
        max: "",
      },
      days: {
        min: "",
        max: "",
      },
      objective: {
        min: "",
        max: "",
      },
    });
    setFilteredData(mainData);
  };

  const handleClickOpenOrderBy = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangeOrderBy = (value) => {
    let sortedData;

    if (value === "relevancia") {
      // Ordenar por porcentaje de completado (de mayor a menor)
      sortedData = [...mainData].sort(
        (a, b) => parseInt(b.completed) - parseInt(a.completed)
      );
    } else if (value === "menorMayor") {
      sortedData = [...mainData].sort(
        (a, b) => parseInt(a.min_investment) - parseInt(b.min_investment)
      );
    } else if (value === "mayorMenor") {
      sortedData = [...mainData].sort(
        (a, b) => parseInt(b.min_investment) - parseInt(a.min_investment)
      );
    }

    setFilteredData(sortedData);
    setAnchorEl(null);
  };

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  const handleCloseMenuOrderBy = () => {
    setAnchorEl(null);
  };

  const GetOwnProjects = () => {
    setBackdropLoading(true);
    loadApi("project", true, "get")
      .then((response) => {
        setMainData(response.data);
        setFilteredData(response.data);
        setPaginatedData(
          response.data.slice(
            (page - 1) * ITEMS_PER_PAGE,
            page * ITEMS_PER_PAGE
          )
        );
        setAllLoaded(true);
      })
      .catch((e) => {
        showSnack(e.message);
      })
      .finally(() => {
        setBackdropLoading(false);
      });
  };
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlerSearchField = ({ target }) => {
    if (target.value.length === 0) {
      setPaginatedData(mainData);
    }

    const tempData = mainData.filter((x) =>
      x.name.toLowerCase().includes(target.value.toLowerCase())
    );
    setFilteredData(tempData);
  };
  const handlerFilterSet = () => {
    const mockUpData = {
      price: {
        min: "",
        max: "",
      },
      profitability: {
        min: "",
        max: "",
      },
      days: {
        min: "",
        max: "",
      },
      objective: {
        min: "",
        max: "",
      },
    };

    let filteredData = mainData;

    // FILTAR INVERSIÓN MINIMA
    if (
      JSON.stringify(mockUpData.price) !== JSON.stringify(filtersSettings.price)
    ) {
      const result = validateMinMaxIsValid(
        filtersSettings.price.min,
        "Inversión mínima",
        filtersSettings.price.max,
        "Inversión máxima",
        showSnack
      );
      if (!result) {
        return;
      }
      filteredData = filteredData.filter(
        (x) =>
          parseInt(x.min_investment) >= filtersSettings.price.min &&
          parseInt(x.min_investment) <= filtersSettings.price.max
      );
    }

    // FILTRAR RENTABILIDAD
    if (
      JSON.stringify(mockUpData.profitability) !==
      JSON.stringify(filtersSettings.profitability)
    ) {
      const result = validateMinMaxIsValid(
        filtersSettings.profitability.min,
        "Rentabilidad mínima",
        filtersSettings.profitability.max,
        "Rentabilidad máxima",
        showSnack
      );
      if (!result) {
        return;
      }
      filteredData = filteredData.filter(
        (x) =>
          parseInt(x.esti_profitability) >= filtersSettings.profitability.min &&
          parseInt(x.esti_profitability) <= filtersSettings.profitability.max
      );
    }

    // FILTRAR DIAS RESTANTES
    if (
      JSON.stringify(mockUpData.days) !== JSON.stringify(filtersSettings.days)
    ) {
      const result = validateMinMaxIsValid(
        filtersSettings.days.min,
        "Días restantes mínimos",
        filtersSettings.days.max,
        "Días restantes máximos",
        showSnack
      );
      if (!result) {
        return;
      }
      filteredData = filteredData.filter(
        (x) =>
          parseInt(x.remaining_days) >= filtersSettings.days.min &&
          parseInt(x.remaining_days) <= filtersSettings.days.max
      );
    }

    // FILTRAR OBJETIVO
    if (
      JSON.stringify(mockUpData.objective) !==
      JSON.stringify(filtersSettings.objective)
    ) {
      const result = validateMinMaxIsValid(
        filtersSettings.objective.min,
        "Objetivo mínimo",
        filtersSettings.objective.max,
        "Objetivo máximo",
        showSnack
      );
      if (!result) {
        return;
      }
      filteredData = filteredData.filter(
        (x) =>
          parseInt(x.objective) >= filtersSettings.objective.min &&
          parseInt(x.objective) <= filtersSettings.objective.max
      );
    }

    setOpenDrawer(false);
    setFilteredData(filteredData);
  };
  const handlerChangeExpanded = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };
  useEffect(() => {
    if (!filteredData) {
      return;
    }
    let tempData = filteredData;
    tempData = tempData.slice(
      (page - 1) * ITEMS_PER_PAGE,
      page * ITEMS_PER_PAGE
    );

    setPaginatedData(tempData);
  }, [page]);

  useEffect(() => {
    if (!filteredData) {
      return;
    }
    let tempData = filteredData;
    tempData = tempData.slice(
      (page - 1) * ITEMS_PER_PAGE,
      page * ITEMS_PER_PAGE
    );
    setPaginatedData(tempData);
  }, [filteredData]);

  useEffect(() => {
    GetOwnProjects();
  }, []);

  return (
    <ManageProjectContext.Provider
      value={{
        openDrawer,
        toggleDrawer,
        expanded,
        handlerChangeExpanded,
        filtersSettings,
        setFiltersSettings,
        handlerFilterSet,
        handlerResetFilters,
      }}
    >
      <GlobalHeader />
      <Stack
        sx={{
          minHeight: "calc(100vh - 120px)",
          width: { xs: "100vw", sm: "95vw", md: "90vw", lg: "75vw" },
          marginInline: "auto",
          pt: 5,
          backgroundColor: "white",
        }}
      >
        <Stack px={{ xs: 2, md: 4 }}>
          <ActionsNav
            title="Mis Proyectos"
            icon={<StackedLineChartIcon fontSize="large" />}
            anchorEl={anchorEl}
            filteredData={filteredData}
            handleChangeOrderBy={handleChangeOrderBy}
            handleClickOpenOrderBy={handleClickOpenOrderBy}
            handleCloseMenuOrderBy={handleCloseMenuOrderBy}
            handlerSearchField={handlerSearchField}
            mainData={mainData}
            openMenuOrderBy={openMenuOrderBy}
            toggleDrawer={toggleDrawer}
            textOrder={{
              first: "Inversión mínima: menor a mayor",
              second: "Inversión máxima: mayor a menor",
            }}
          />
          {allLoaded && filteredData.length === 0 ? (
            <Stack pt={3}>
              <Typography color="GrayText" textAlign="center">
                No se encontraron proyectos
              </Typography>
            </Stack>
          ) : (
            ""
          )}
          {allLoaded && (
            <Stack pt={3} pb={6}>
              <ContainerCardsOwn
                types="project"
                columns={3}
                data={paginatedData}
                childSizes={{ xs: 3, sm: 1.5, md: 1, lg: 1 }}
                editButton={true}
                detailsButton={true}
                deleteButton={true}
                showVerified={true}
                setPaginatedData={setPaginatedData}
              />
              {allLoaded && filteredData.length !== 0 ? (
                <Pagination
                  count={Math.ceil(filteredData.length / ITEMS_PER_PAGE)}
                  sx={{ marginInline: "auto", mt: 4 }}
                  page={page}
                  onChange={handlePageChange}
                />
              ) : (
                ""
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Footer />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdropLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <FilterOwnProjects />
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </ManageProjectContext.Provider>
  );
}

export default MyProjects;
